import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import {
  ConfirmDialogService,
  ConfirmDialogStatus,
  DialogService,
  FormSwitchComponent,
  isEmptyTable,
  SaveForm,
  selectAllRentals,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import * as fromRoot from '@tv3/store/state'
import { select, Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import {
  AutomationViewGroup,
  AutomationFormData,
  PauseAutomation,
  UnPauseAutomation,
  DeleteAutomation,
  AddAutomationComplete,
  DeleteAutomationComplete,
  AutomationTypes,
  AutomationHelpers,
  AutomationHandler,
  AutomationServiceTypes,
  AutomationEventTypes,
  selectIsAddingAutomation,
  selectIsUpdatingAutomation,
} from '@tokeet-frontend/automations'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { AutomationTab } from '../../tabs'
import { channelReviewSourceOptions, ChannelReviewSources } from '@tokeet-frontend/reviews'
import { selectTemplatesByTypes, Template, TemplateType } from '@tokeet-frontend/templates'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { map, startWith } from 'rxjs/operators'
import { combineLatest, Observable } from 'rxjs'
import { AutomationDestroyableSaver } from '@tv3/containers/automations/automation-destroyable-saver'
import { AppOverviewDialogService } from '@tokeet-frontend/billing'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { TemplateDialogService } from '@tv3/containers/templates/template-dialog/template-dialog.service'
@Component({
  selector: 'app-review-automation-overlay',
  templateUrl: './review-automation-overlay.component.html',
  styleUrls: ['./review-automation-overlay.component.scss'],
})
export class ReviewAutomationOverlayComponent extends AutomationDestroyableSaver implements OnInit {
  tab = AutomationTab.Overview
  tabs = AutomationTab

  @ViewChild('paginator') set paginator(p: MatPaginator) {
    this.dataSource.paginator = p
  }
  @ViewChild(MatSort) set sort(s: MatSort) {
    this.dataSource.sort = s
  }

  displayedColumns = ['name', 'body', 'rating', 'edit']
  dataSource = new MatTableDataSource<Template>()
  isEmptyTable$ = isEmptyTable(this.dataSource)

  timeeventForm = this.fb.group({
    event: [AutomationEventTypes.TimeAfterBookingCheckout],
    days: [0, [Validators.required, Validators.max(14)]],
    hours: [5, [Validators.required, Validators.min(4), Validators.max(23)]],
  })
  form: FormGroup = this.fb.group({
    status: [true],
    service: [AutomationServiceTypes.Advance, [Validators.required]],
    type: [AutomationTypes.Review, [Validators.required]],
    name: ['', [Validators.required]],
    event: ['time', [Validators.required]],
    timeevent: this.timeeventForm,
    channels: [[ChannelReviewSources.Airbnb], [Validators.required]],
    rentals: [],
    handler: [AutomationHandler.SendReview],
    // Review
    default: [5], // default rating
    templates: [[], [Validators.required]],
    ai_enabled: [false],
  })

  get templatesCtrl() {
    return this.form.get('templates') as FormControl
  }

  rentals$ = this.store.pipe(select(selectAllRentals))
  channels = channelReviewSourceOptions.filter((t) => t.automation)
  templates$ = this.store.pipe(select(selectTemplatesByTypes([TemplateType.Review])))

  isEdit = false
  get isDuplicate() {
    return this.data?.duplicate
  }

  get automation() {
    return this.data?.automationGroup
  }

  isAdding$: Observable<boolean> = this.store.select(selectIsAddingAutomation)
  isUpdating$: Observable<boolean> = this.store.select(selectIsUpdatingAutomation)

  isSaving$: Observable<boolean> = combineLatest([this.isAdding$, this.isUpdating$]).pipe(
    map(([isAdding, isUpdating]) => isAdding || isUpdating)
  )

  isAISubscribed = false

  constructor(
    public dialogRef: MatDialogRef<ReviewAutomationOverlayComponent>,
    private fb: FormBuilder,
    private actions: Actions,
    private dialog: DialogService,
    private confirmDialogService: ConfirmDialogService,
    private appOverviewDialog: AppOverviewDialogService,
    private templateDialogService: TemplateDialogService,
    amplitudeService: AmplitudeService,
    store: Store<fromRoot.State>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      automationGroup?: AutomationViewGroup
      defaults?: Partial<AutomationFormData>
      duplicate?: boolean
    }
  ) {
    super(store, amplitudeService)
  }

  ngOnInit() {
    if (this.automation) {
      this.isEdit = !this.data.duplicate

      this.form.patchValue({
        ...this.automation,
        templates: this.automation.templates || this.automation.settings?.templates || [],
        ai_enabled: this.automation.ai_enabled || this.automation.settings?.ai_enabled,
        default: this.automation.settings?.reviews?.default || 5,
        status: !!this.automation.status,
        name: this.automation.attributes?.name,
        event: AutomationHelpers.getAutomationEvent(this.automation.event),
        timeevent: {
          event: this.automation.timeevent,
          hours: this.automation?.settings?.hours,
          days: this.automation?.settings?.days,
        },
      })
    } else if (this.data.defaults) {
      this.form.patchValue(this.data.defaults)
    }

    this.store.pipe(select(selectActiveProductPlanView(ProductsForPlan.TokeetAI))).subscribe((sub) => {
      this.isAISubscribed = sub?.isValid
    })

    this.actions.pipe(ofType(AddAutomationComplete, DeleteAutomationComplete), untilDestroy(this)).subscribe(() => {
      this.close()
    })

    this.form
      .get('ai_enabled')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((enabled) => {
        if (enabled) {
          this.templatesCtrl.clearValidators()
        } else {
          this.templatesCtrl.setValidators([Validators.required])
        }
        this.templatesCtrl.updateValueAndValidity()
      })

    combineLatest([this.templates$, this.templatesCtrl.valueChanges.pipe(startWith(this.templatesCtrl.value || []))])
      .pipe(untilDestroy(this))
      .subscribe(([templates, ids]) => {
        this.dataSource.data = templates.filter((t) => ids?.includes(t.id))
      })
  }

  onRemoveTemplate(template: Template) {
    const selected = this.templatesCtrl.value || []
    this.templatesCtrl.setValue(selected.filter((id) => id !== template.id))
  }

  onTogglePause(checked: boolean) {
    if (!checked) {
      this.store.dispatch(PauseAutomation({ ids: this.automation.ids }))
    } else {
      this.store.dispatch(UnPauseAutomation({ ids: this.automation.ids }))
    }
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { ...payload } = form.getRawValue()
    this.save({ ...payload }, this.isEdit ? this.automation : null)
  }

  onToggleAI(enabled: boolean, ctrl: FormSwitchComponent) {
    if (this.isAISubscribed) {
      if (!enabled) return
      this.confirmDialogService
        .openRegular({
          title: $localize`Enable AI Generated Review?`,
          body: $localize`Please confirm if you want to allow AI to generate review for guest based to assigned booking rating.`,
        })
        .subscribe((status) => {
          if (status !== ConfirmDialogStatus.Confirmed) {
            ctrl.checked = false
          }
        })
      return
    }
    this.form.get('ai_enabled').setValue(false)
    this.appOverviewDialog.open(ProductsForPlan.TokeetAI)
  }

  onDelete() {
    this.confirmDialogService
      .confirm({
        title: $localize`Delete this automation?`,
        body: $localize`Are you sure you want to delete this automation?`,
      })
      .subscribe(() => {
        this.store.dispatch(DeleteAutomation({ ids: this.automation.ids }))
      })
  }

  onNewTemplate() {
    this.templateDialogService.open({ type: TemplateType.Review, hideType: true, hideTemplates: true })
  }

  onEditTemplate(template: Template) {
    this.templateDialogService.open({ template, hideType: true, hideTemplates: true })
  }

  close() {
    this.dialogRef.close()
  }
}
